import { Component, HostListener, OnInit, ChangeDetectionStrategy } from "@angular/core";

import { BaseComponent, UserService, EventBusService } from "@mypxplat/xplat/core";

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "myp-image-viewer",
  templateUrl: "image-viewer.component.html",
  standalone: false,
  styles: [
    `
      .unviewable-photo {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        text-align: center;
        padding: 20px;
        overflow-wrap: anywhere;
      }
    `,
  ],
})
export class ImageViewerComponent extends BaseComponent implements OnInit {
  // Emitted payload is either a bare url (single image, no arrows) or
  // `{images: string[], index: number}` for a browsable set with prev/next arrows.
  public images: string[] = [];
  public currentIndex: number = 0;

  constructor(
    userService: UserService,
    private _eventbusService: EventBusService
  ) {
    super(userService);
  }

  ngOnInit() {
    this._eventbusService.observe(this._eventbusService.types.viewFullscreenImage).subscribe((result) => {
      if (typeof result === "string") {
        this.images = result ? [result] : [];
        this.currentIndex = 0;
      } else if (result?.images?.length) {
        this.images = result.images;
        this.currentIndex = result.index >= 0 && result.index < result.images.length ? result.index : 0;
      } else {
        this.images = [];
      }
    });
  }

  // Urls this browser failed to decode, so navigating away and back keeps
  // showing the fallback instead of retrying a load that can't succeed.
  private failed = new Set<string>();

  get src(): string | undefined {
    return this.images[this.currentIndex];
  }

  get srcFailed(): boolean {
    return !!this.src && this.failed.has(this.src);
  }

  markFailed() {
    if (this.src) this.failed.add(this.src);
  }

  /** Getter rather than a template call, so change detection isn't re-parsing the url. */
  get srcFileName(): string {
    return this.fileName(this.src);
  }

  private fileName(url: string | undefined): string {
    if (!url) return "";
    try {
      const path = new URL(url, window.location.origin).pathname;
      return decodeURIComponent(path.split("/").pop() || url);
    } catch {
      return url.split("?")[0].split("/").pop() || url;
    }
  }

  get isOpen(): boolean {
    return this.images.length > 0;
  }

  close() {
    this.images = [];
  }

  prev(event?: Event) {
    event?.stopPropagation();
    if (this.images.length < 2) return;
    this.currentIndex = (this.currentIndex - 1 + this.images.length) % this.images.length;
  }

  next(event?: Event) {
    event?.stopPropagation();
    if (this.images.length < 2) return;
    this.currentIndex = (this.currentIndex + 1) % this.images.length;
  }

  @HostListener("document:keydown", ["$event"])
  handleKeydown(event: KeyboardEvent) {
    if (!this.isOpen) return;
    if (event.key === "Escape") this.close();
    else if (event.key === "ArrowLeft") this.prev();
    else if (event.key === "ArrowRight") this.next();
  }
}
